import React, { useState, useCallback } from 'react';
import { X, Upload, FileSpreadsheet, CheckCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { firmaAPI } from '../api';
import toast from 'react-hot-toast';

const KOLONLAR = ['sira', 'firma_adi', 'ilce', 'ilgili_kurum', 'mail', 'telefon', 'notlar'];

function normalize(str) {
  return (str || '').toString().toLowerCase().trim()
    .replace(/ı/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u')
    .replace(/ş/g, 's').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/\s+/g, ' ').trim()
    .replace(/[^a-z0-9 ]/g, '');
}

function guessMapping(headers) {
  const map = {};
  const hints = {
    sira: ['sira', 'no', 'numara', 's ra'],
    firma_adi: ['firma', 'ad', 'isim', 'name', 'company', 'firma adi', 'sirket'],
    ilce: ['ilce', 'bolge', 'osb', 'sehir', 'ilce osb', 'i lce'],
    ilgili_kurum: ['kurum', 'kurulus', 'institution', 'ilgili kurum'],
    mail: ['mail', 'email', 'eposta', 'mail adresi', 'e posta'],
    telefon: ['telefon', 'tel', 'gsm', 'phone', 'telefon no'],
    notlar: ['not', 'aciklama', 'note', 'notlar'],
  };
  KOLONLAR.forEach((col) => {
    for (const h of headers) {
      const n = normalize(h);
      if (hints[col]?.some((hint) => n.includes(hint) || hint.includes(n))) {
        if (!map[col]) map[col] = h;
        break;
      }
    }
  });
  return map;
}

export default function ImportModal({ onClose, onImported }) {
  const [step, setStep] = useState('upload');
  const [rawData, setRawData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);

  const processFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      if (!rows.length) { toast.error('Dosya boş'); return; }
      const hdrs = Object.keys(rows[0]).filter(h => h && String(h).trim());
      setHeaders(hdrs);
      setRawData(rows);
      const autoMap = guessMapping(hdrs);
      setMapping(autoMap);
      setStep('map');
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, []);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  };

  const handlePreview = () => {
    const mapped = rawData.slice(0, 10).map((row) => {
      const obj = {};
      KOLONLAR.forEach((col) => { obj[col] = mapping[col] ? row[mapping[col]] : ''; });
      return obj;
    });
    setPreview(mapped);
    setStep('preview');
  };

  const handleImport = async () => {
    setLoading(true);
    const items = rawData.map((row) => {
      const obj = {};
      KOLONLAR.forEach((col) => {
        const raw = mapping[col] ? row[mapping[col]] : '';
        obj[col] = raw !== null && raw !== undefined ? String(raw).trim() : '';
      });
      return obj;
    }).filter((r) => r.firma_adi && r.firma_adi.trim());

    try {
      const res = await firmaAPI.bulkImport(items);
      setResult(res.data);
      setStep('done');
      onImported();
      toast.success(`${res.data.count} firma aktarıldı!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aktarım hatası');
    } finally {
      setLoading(false);
    }
  };

  const stepLabels = { upload: 'Dosya Yükle', map: 'Sütun Eşleştir', preview: 'Önizleme', done: 'Tamamlandı' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop bg-navy-900/40">
      <div className="modal-content bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy-100">
          <h2 className="font-display text-lg text-navy-800">{stepLabels[step]}</h2>
          <button onClick={onClose} className="btn-ghost p-1.5"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {step === 'upload' && (
            <div
              className={`border-2 border-dashed rounded-2xl p-12 text-center transition-colors ${dragging ? 'border-navy-400 bg-navy-50' : 'border-navy-200 hover:border-navy-300'}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
            >
              <FileSpreadsheet size={40} className="mx-auto text-navy-300 mb-3" />
              <p className="text-navy-600 font-medium mb-1">Excel veya CSV dosyasını sürükle</p>
              <p className="text-navy-400 text-sm mb-4">.xlsx, .xls, .csv desteklenir</p>
              <label className="btn-primary cursor-pointer">
                <Upload size={16} /> Dosya Seç
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
              </label>
            </div>
          )}

          {step === 'map' && (
            <div>
              <p className="text-navy-500 text-sm mb-4">{rawData.length} satır yüklendi. Sütunları kontrol edin:</p>
              <div className="space-y-3">
                {KOLONLAR.map((col) => (
                  <div key={col} className="flex items-center gap-3">
                    <span className={`text-xs font-semibold uppercase tracking-wide w-36 flex-shrink-0 ${col === 'firma_adi' ? 'text-navy-700' : 'text-navy-400'}`}>
                      {col.replace('_', ' ')} {col === 'firma_adi' && <span className="text-rose-500">*</span>}
                    </span>
                    <select
                      className="select flex-1"
                      value={mapping[col] || ''}
                      onChange={(e) => setMapping({ ...mapping, [col]: e.target.value || undefined })}
                    >
                      <option value="">— Eşleştirme yok —</option>
                      {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {mapping[col] && <span className="text-emerald-500 text-xs">✓</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div>
              <p className="text-navy-500 text-sm mb-3">İlk 10 satır — toplam <strong>{rawData.length}</strong> firma aktarılacak</p>
              <div className="overflow-x-auto rounded-xl border border-navy-100">
                <table className="w-full text-xs">
                  <thead>
                    <tr>{['firma_adi','ilce','ilgili_kurum','mail','telefon'].map((c) => <th key={c} className="th">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} className="table-row">
                        {['firma_adi','ilce','ilgili_kurum','mail','telefon'].map((c) => (
                          <td key={c} className="td truncate max-w-[140px]">{row[c]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-8">
              <CheckCircle size={48} className="mx-auto text-emerald-500 mb-3" />
              <p className="font-display text-xl text-navy-800 mb-1">Aktarım Tamamlandı</p>
              <p className="text-navy-500 text-sm"><strong>{result?.count}</strong> firma başarıyla içe aktarıldı.</p>
            </div>
          )}
        </div>

        <div className="flex justify-between gap-2 px-5 py-4 border-t border-navy-100">
          <div>
            {step === 'map' && <button className="btn-secondary" onClick={() => setStep('upload')}>Geri</button>}
            {step === 'preview' && <button className="btn-secondary" onClick={() => setStep('map')}>Geri</button>}
          </div>
          <div>
            {step === 'upload' && <button className="btn-secondary" onClick={onClose}>İptal</button>}
            {step === 'map' && <button className="btn-primary" onClick={handlePreview} disabled={!mapping['firma_adi']}>Önizle →</button>}
            {step === 'preview' && (
              <button className="btn-gold" onClick={handleImport} disabled={loading}>
                {loading && <span className="w-3.5 h-3.5 border-2 border-navy-400/40 border-t-navy-900 rounded-full animate-spin" />}
                {rawData.length} Firmayı Aktar
              </button>
            )}
            {step === 'done' && <button className="btn-primary" onClick={onClose}>Kapat</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
